// Body-composition maths shared by every surface that shows a client's
// numbers. Extracted from ProfileCard so the compact profile strip in the Users
// split view can't drift from the full card — two places computing BMI or BMR
// slightly differently is a clinical-looking bug.
//
// All functions are pure and return null for unusable input rather than NaN or
// a placeholder string, so callers decide how a missing value renders.

/**
 * Newest entry in a `created_at`-stamped list, or null.
 * An entry with a missing/invalid date for `field` sorts as oldest (rather
 * than NaN, which would make every comparison false and "stick" the reduce
 * on whichever entry happens to come first in the array).
 */
export function getLatestEntry(entries, field = "created_at") {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const timeOf = (entry) => {
    const t = new Date(entry?.[field]).getTime();
    return Number.isNaN(t) ? -Infinity : t;
  };
  return entries.reduce((latest, current) =>
    timeOf(current) > timeOf(latest) ? current : latest
  );
}

/**
 * Whole years between `dob` and today.
 * @param {string} dob
 * @returns {number|null} Null when absent or unparseable.
 */
export function calculateAge(dob) {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

/**
 * Normalise a height to centimetres.
 * @param {number|string} height
 * @param {string} units - cm | mm | inches | feet | ft. Anything else is cm.
 * @returns {number|null}
 */
export function convertHeightToCm(height, units) {
  if (!height || Number.isNaN(Number(height))) return null;
  const h = Number(height);
  if (units === "cm") return h;
  if (units === "mm") return h / 10;
  if (units === "inches") return h * 2.54;
  if (units === "feet" || units === "ft") return h * 30.48;
  return h; // default assume cm
}

/**
 * Normalise a weight to kilograms.
 * @param {number|string} weight
 * @param {string} units - kg | g | lbs | pounds. Anything else is kg.
 * @returns {number|null}
 */
export function convertWeightToKg(weight, units) {
  if (!weight || Number.isNaN(Number(weight))) return null;
  const w = Number(weight);
  if (units === "kg") return w;
  if (units === "g") return w / 1000;
  if (units === "lbs" || units === "pounds") return w * 0.453592;
  return w; // default assume kg
}

/** True for a gender description that reads as female. */
function isFemaleGender(gender) {
  const lower = (gender || "").toLowerCase();
  return lower.includes("female") || lower.includes("f");
}

/**
 * BMI to one decimal.
 * @returns {number|null}
 */
export function calculateBMI(weight, height, weightUnits, heightUnits) {
  const w = convertWeightToKg(weight, weightUnits);
  const h = convertHeightToCm(height, heightUnits);
  if (!w || !h || h === 0) return null;
  const metres = h / 100;
  return parseFloat((w / (metres * metres)).toFixed(1));
}

/**
 * Ideal weight in kg (Broca index, floored at 50kg — the floor is existing
 * product behaviour, not a clinical rule).
 * @returns {number|null}
 */
export function calculateIdealWeight(height, gender, heightUnits) {
  const h = convertHeightToCm(height, heightUnits);
  if (!h) return null;
  const ideal = isFemaleGender(gender) ? h - 105 : h - 100;
  return Math.round(Math.max(ideal, 50));
}

/**
 * Basal metabolic rate, kcal/day (Mifflin-St Jeor).
 * @returns {number|null}
 */
export function calculateBMR(weight, height, age, gender, weightUnits, heightUnits) {
  const w = convertWeightToKg(weight, weightUnits);
  const h = convertHeightToCm(height, heightUnits);
  const a = Number(age);
  if (!w || !h || !a || Number.isNaN(a) || a <= 0) return null;
  let bmr = 10 * w + 6.25 * h - 5 * a;
  bmr += isFemaleGender(gender) ? -161 : 5;
  return Math.round(bmr);
}

/**
 * Total daily energy expenditure, kcal/day.
 * @param {number|null} bmr
 * @param {number} [activityLevel=1.2] - Sedentary by default, as elsewhere.
 * @returns {number|null}
 */
export function calculateTDEE(bmr, activityLevel = 1.2) {
  if (!bmr || Number.isNaN(bmr)) return null;
  return Math.round(bmr * activityLevel);
}

/**
 * Standard WHO BMI band for a value, so a bare number can carry its meaning.
 * `tone` is a semantic name, not a colour — callers map it to their own palette.
 *
 * @param {number|null} bmi
 * @returns {{label: string, tone: 'low'|'normal'|'warn'|'high'}|null}
 */
export function bmiCategory(bmi) {
  if (bmi == null || Number.isNaN(bmi)) return null;
  if (bmi < 18.5) return { label: "Underweight", tone: "low" };
  if (bmi < 25) return { label: "Normal", tone: "normal" };
  if (bmi < 30) return { label: "Overweight", tone: "warn" };
  return { label: "Obese", tone: "high" };
}
